<template>
	<div class="section">
		<div class="container">
			<h1 class="title is-1">Methodology</h1>
			<p>
				Compliance means that CTC is fully and effectively implemented, prevails over conflicting
				law, and is being interpreted and applied in accordance with its terms and intent. For
				purposes of the foregoing:
			</p>
			<ol type="a">
				<li>
					'conflicting law' means any law, rule, regulation, or regulatory practice that conflicts
					with, conditions, qualifies, or imposes a cost on CTC rights or remedies (beyond that
					expressly permitted by CTC); and
				</li>
				<li>
					the 'terms and intent' of CTC based on the Official Commentary on the Convention on
					International Interests in Mobile Equipment and the Protocol thereto on Matters Specific
					to Aircraft Equipment, by Professor Sir Roy Goode (Fifth edition 2022) (the
					<b>Official Commentary</b>
					) and annotations thereto issued by the Cape Town Convention Academic Project from time to
					time (the
					<b>Annotations</b>
					).
				</li>
			</ol>
			<p>
				The Official Commentary is produced by Professor Goode pursuant to Resolution No. 5 of the
				Cape Town Diplomatic Conference and is the authoritative source for those seeking to
				understand and interpret the Convention. Professor Goode
				<a
					target="_blank"
					href="http://cdm15895.contentdm.oclc.org/cdm/ref/collection/p15895coll11/id/52">
					personally endorsed
				</a>
				the Cape Town Convention Academic Project, under the auspices of the University of Cambridge
				and UNIDROIT, to prepare and issue annotations to the Official Commentary to address
				questions that the Official Commentary does not, or does not fully, address.
			</p>
			<p>
				A category (reflecting both compliance and anticipated future compliance) is assigned to
				each contracting state in accordance with the final score calculated per the methodology. In
				exceptional circumstances, a category override may be deemed necessary and applied in
				accordance with the methodology.
			</p>
			<p>
				The Index calculates each contracting state's CTC compliance score based on a formula
				comprised of six (6) variables:
			</p>
			<p>Score = 5(A + B) + 2.5(C + D+ E + F)</p>
			<ol type="i">
				<li>
					Variable A (de jure compliance), which relates to the legal implementation of CTC in a
					contracting state, with regard to primacy (whether CTC prevails over conflicting national
					law) and completeness (whether rules, regulations and legislation are in place to fully
					implement CTC);
				</li>
				<li>
					Variable B (de facto compliance), which considers reported administrative and judicial
					precedent, and experience, advised by experts, in applying and enforcing CTC in a
					contracting state;
				</li>
				<li>
					Variable C, which considers whether a country is and should remain eligible for a 'CTC
					discount' under the OECD Aircraft Sector Understanding and whether it has been removed
					from such eligibility due to non-compliance with CTC;
				</li>
				<li>
					Variable D, which reflects whether the applicable contracting state has designated a
					'communications channel' with AWG to consult on and address compliance issues, and the
					extent to which the results of such consultations are timely and effective;
				</li>
				<li>
					Variable E, which is the result of a jurisdictional analysis of legal rules and practices,
					other than those covered by variables A, B, and F, which assesses the alignment of
					existing rules with CTC requirements and indicates anticipated compliance with CTC; and
				</li>
				<li>
					Variable F, which is a weighing of select rule of law and political risk indices relating
					to the applicable contracting state measuring, or materially relevant to, regulatory
					enforcement of existing law and protection of property rights.
				</li>
			</ol>
			<p>
				Variables A and B, as the directly CTC-related variables, together comprise 50% of the
				score, weighed equally. Variables C, D, E and F, as proxies indicating anticipated CTC
				compliance, together comprise the remaining 50%, also weighed equally.
			</p>
			<p>
				The formula also includes an internal mechanism to adjust the weight of relevant variables
				in order to give more weight to direct CTC precedent and practice, where such precedent or
				practice exist. It does so by applying a precedent-based adjustment (PBA) where a direct CTC
				precedent exists in a contracting state. In such cases, the weight assigned to Variable B
				will be increased and, correspondingly, the weights assigned to Variables E and F, as
				proxies for anticipated compliance, will decrease. The amount of such increase/decrease
				depends on the qualitative nature of the existing precedent and the level of predictive
				value it has for future cases with similar facts.
			</p>
			<p>
				For the three variables that require legal facts and analysis - variables A, B, and E - AWG
				developed a form questionnaire related to such variables. The form of the questionnaire can
				be reviewed here.
			</p>
			<p>
				In order for a contracting state to be assessed and scored under the Index, AWG requires
				that between two to ten law firms with expertise in aviation provide consensus responses to
				the questionnaire. To the extent possible, the foremost legal experts in the field of
				aviation law in each country were selected, drawing from recommendations from experienced
				leaders in aviation finance and aircraft leasing. AWG thanks all the law firms that
				contributed their time, expertise, and energy to providing responses to the questionnaires,
				including the ones that have agreed to be named here.
			</p>
			<p>
				Other sources of data considered are (1) AWG's direct experience, and those of its members,
				in working with civil aviation authorities and other government offices in connection with
				the exercise of CTC rights and remedies, (2) the views of the Executive Committee of its
				Legal Advisory Panel, (3) Pillsbury's World Aircraft Repossession Index, (4) surveys of
				existing literature, and (5) other input from legal practitioners and other experts.
			</p>
			<p>
				The full methodology with more detailed information on each variable and the analytical
				process for the Index is available for review
				<a target="_blank" href="/CTC Compliance Index - Methodology.pdf">here</a>
				.
			</p>
		</div>
	</div>
</template>

<script>
	export default {
		created() {},
		mounted() {}
	};
</script>
